import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { 
  FiChevronLeft, 
  FiChevronRight, 
  FiEdit2, 
  FiTrash2, 
  FiCheck, 
  FiChevronDown, 
  FiChevronUp, 
  FiClock, 
  FiLayers, 
  FiTag, 
  FiCalendar, 
  FiDroplet, 
  FiShoppingBag 
} from 'react-icons/fi';
import { WORKFLOW_STAGES, PRODUCT_TYPES, TIMELINE_COLORS } from '../../data/constants';
import ChecklistItem from '../ShopSetup/ChecklistItem';

const ProductCard = ({ product, updateProduct, deleteProduct, moveProduct, shops, protocols = [] }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeProtocolId, setActiveProtocolId] = useState('');

  useEffect(() => {
    if (protocols.length > 0 && !activeProtocolId) {
      setActiveProtocolId(protocols[0].id);
    }
  }, [protocols, isExpanded]);

  const [editForm, setEditForm] = useState({
    title: product.title || '',
    types: product.types || ['T-Shirt'],
    priority: product.priority || 'Medium',
    startDate: product.startDate || new Date().toISOString().split('T')[0],
    targetDate: product.targetDate || new Date().toISOString().split('T')[0],
    timelineColor: product.timelineColor || TIMELINE_COLORS[0].value,
    shopIds: product.shopIds || []
  });

  const stageIndex = WORKFLOW_STAGES.indexOf(product.status);
  const canMoveLeft = stageIndex > 0;
  const canMoveRight = stageIndex < WORKFLOW_STAGES.length - 1;

  const handleSave = () => {
    updateProduct(product.id, editForm);
    setIsEditing(false);
  };

  const handleShopToggle = (shopId) => {
    setEditForm(prev => {
      const ids = prev.shopIds.includes(shopId) 
        ? prev.shopIds.filter(id => id !== shopId) 
        : [...prev.shopIds, shopId];
      return { ...prev, shopIds: ids };
    });
  };

  const getSectionProgress = (tasks) => {
    if (!tasks || tasks.length === 0) return 0;
    const completed = tasks.filter(item => {
      const d = product.checklist?.[item.id];
      return typeof d === 'object' ? d.completed : !!d;
    }).length;
    return Math.round((completed / tasks.length) * 100);
  };

  const activeProtocol = protocols.find(p => p.id === activeProtocolId) || protocols[0];

  if (isEditing) {
    return (
      <div className="bg-white p-5 rounded-2xl border-2 border-sage shadow-xl mb-4 animate-fade-in relative z-10 space-y-4 max-h-[620px] overflow-y-auto custom-scrollbar text-espresso">
        <div>
          <label className="text-[10px] font-black uppercase text-espresso-light/40 tracking-widest mb-1 block">Title</label>
          <input 
            className="w-full text-2xl font-bold border-b border-cream-dark pb-1 focus:outline-none focus:border-sage bg-transparent" 
            value={editForm.title}
            onChange={e => setEditForm({...editForm, title: e.target.value})}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] font-black uppercase text-espresso-light/40 tracking-widest mb-1 block">Product Type</label>
            <select 
              className="w-full text-xs font-bold bg-cream/30 p-2 rounded-lg border border-cream-dark focus:outline-none"
              value={editForm.types[0]}
              onChange={e => setEditForm({...editForm, types: [e.target.value]})}
            >
              {PRODUCT_TYPES.map(type => <option key={type}>{type}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase text-espresso-light/40 tracking-widest mb-1 block">Priority</label>
            <select 
              className="w-full text-xs font-bold bg-cream/30 p-2 rounded-lg border border-cream-dark focus:outline-none"
              value={editForm.priority}
              onChange={e => setEditForm({...editForm, priority: e.target.value})}
            >
              <option>Low</option>
              <option>Medium</option>
              <option>High</option>
              <option>Urgent</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] font-black uppercase text-espresso-light/40 tracking-widest mb-1 block">Start Date</label>
            <input 
              type="date" 
              className="w-full text-xs font-bold bg-cream/30 p-2 rounded-lg border border-cream-dark focus:outline-none"
              value={editForm.startDate}
              onChange={e => setEditForm({...editForm, startDate: e.target.value})}
            />
          </div>
          <div>
            <label className="text-[10px] font-black uppercase text-espresso-light/40 tracking-widest mb-1 block">Deadline</label>
            <input 
              type="date" 
              className="w-full text-xs font-bold bg-cream/30 p-2 rounded-lg border border-cream-dark focus:outline-none"
              value={editForm.targetDate}
              onChange={e => setEditForm({...editForm, targetDate: e.target.value})}
            />
          </div>
        </div>
        <div>
          <label className="text-[10px] font-black uppercase text-espresso-light/40 tracking-widest mb-2 block flex items-center gap-1">
            <SafeIcon icon={FiDroplet} className="w-2.5 h-2.5" /> Timeline Color
          </label>
          <div className="flex gap-2">
            {TIMELINE_COLORS.map(color => (
              <button 
                key={color.value}
                type="button"
                onClick={() => setEditForm({...editForm, timelineColor: color.value})}
                className={`w-6 h-6 rounded-full border-2 transition-all ${editForm.timelineColor === color.value ? 'border-espresso scale-110 shadow-md' : 'border-transparent'}`}
                style={{ backgroundColor: color.value }}
                title={color.name}
              />
            ))}
          </div>
        </div>
        <div>
          <label className="text-[10px] font-black uppercase text-espresso-light/40 tracking-widest mb-2 block">Assigned Shops</label>
          <div className="flex flex-wrap gap-2">
            {shops.map(shop => (
              <button 
                key={shop.id}
                onClick={() => handleShopToggle(shop.id)}
                className={`px-2 py-1 rounded-md text-xs font-bold border transition-all ${editForm.shopIds.includes(shop.id) ? 'bg-terracotta text-white border-terracotta' : 'bg-cream text-espresso-light/40 border-cream-dark hover:border-espresso-light'}`}
              >
                {shop.name}
              </button>
            ))}
          </div>
        </div>
        <div className="flex justify-between items-center pt-4 border-t border-cream-dark">
          <button onClick={() => deleteProduct(product.id)} className="text-xs font-bold uppercase text-red-400 hover:text-red-600 flex items-center gap-1">
            <SafeIcon icon={FiTrash2} /> Delete
          </button>
          <div className="flex gap-2">
            <button onClick={() => setIsEditing(false)} className="text-xs font-bold uppercase px-4 py-2 text-espresso-light">Cancel</button>
            <button onClick={handleSave} className="text-xs font-bold uppercase px-6 py-2 rounded-xl bg-sage text-white shadow-md">Save</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-2xl border transition-all duration-300 mb-4 group hover:shadow-xl ${isExpanded ? 'border-sage ring-4 ring-sage/5' : 'border-cream-dark shadow-sm'}`}>
      <div className="p-5">
        <div className="flex justify-between items-start mb-3">
          <span className="text-[10px] font-black text-terracotta bg-terracotta/5 px-2 py-0.5 rounded border border-terracotta/20 uppercase tracking-tighter">
            {product.designNumber}
          </span>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button 
              onClick={() => setIsExpanded(!isExpanded)}
              className={`p-1.5 rounded-lg transition-colors ${isExpanded ? 'text-sage bg-sage/10' : 'text-espresso-light/40 hover:text-espresso hover:bg-cream'}`}
            >
              <SafeIcon icon={isExpanded ? FiChevronUp : FiChevronDown} className="w-4 h-4" />
            </button>
            <button 
              onClick={() => setIsEditing(true)}
              className="p-1.5 text-espresso-light/40 hover:text-espresso hover:bg-cream transition-colors rounded-lg"
            >
              <SafeIcon icon={FiEdit2} className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-4 h-4 rounded-full shadow-sm flex-shrink-0" style={{ backgroundColor: product.timelineColor || TIMELINE_COLORS[0].value }} />
          <h4 className="font-bold text-3xl text-espresso leading-[1.1] group-hover:text-terracotta transition-colors">
            {product.title || 'Untitled Design'}
          </h4>
        </div>

        {/* DATA DISPLAY SECTION: Type & Shop */}
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="text-[10px] font-black uppercase px-2 py-1 bg-cream-dark/30 text-espresso-light rounded-md flex items-center gap-1.5">
            <SafeIcon icon={FiTag} className="w-3 h-3" /> 
            {product.types?.[0] || 'T-Shirt'}
          </span>
          {product.shopIds?.map(sid => {
            const shop = shops.find(s => s.id === sid);
            if (!shop) return null;
            return (
              <span key={sid} className="text-[10px] font-black uppercase px-2 py-1 bg-terracotta/10 text-terracotta rounded-md flex items-center gap-1.5">
                <SafeIcon icon={FiShoppingBag} className="w-3 h-3" />
                {shop.name}
              </span>
            );
          })}
        </div>

        {/* TIMELINE DISPLAY SECTION */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-black text-espresso-light/40 uppercase tracking-widest flex items-center gap-1">
              <SafeIcon icon={FiClock} className="w-2.5 h-2.5" /> Timeline
            </span>
            <div className="text-xs font-bold text-espresso-light">
              {product.startDate ? new Date(product.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '--'} - {product.targetDate ? new Date(product.targetDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '--'}
            </div>
          </div>
          <div className="flex flex-col gap-1 text-right">
            <span className="text-[10px] font-black text-espresso-light/40 uppercase tracking-widest">Master Done</span>
            <div className="text-sm font-black text-sage">
              {protocols.length > 0 ? (
                Math.round(protocols.reduce((acc, p) => acc + getSectionProgress(p.tasks), 0) / protocols.length)
              ) : 0}%
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center pt-3 border-t border-cream-dark/50">
          <button 
            onClick={() => moveProduct(product.id, -1)} 
            disabled={!canMoveLeft}
            className={`p-1.5 rounded-lg ${canMoveLeft ? 'text-espresso hover:bg-cream-dark' : 'text-cream-dark cursor-not-allowed'}`}
          >
            <SafeIcon icon={FiChevronLeft} className="w-4 h-4" />
          </button>
          <span className="text-[10px] font-black text-espresso-light/40 uppercase tracking-widest">{product.status}</span>
          <button 
            onClick={() => moveProduct(product.id, 1)} 
            disabled={!canMoveRight}
            className={`p-1.5 rounded-lg ${canMoveRight ? 'text-espresso hover:bg-cream-dark' : 'text-cream-dark cursor-not-allowed'}`}
          >
            <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-cream-dark bg-cream/10 animate-fade-in rounded-b-2xl overflow-hidden">
          <div className="flex border-b border-cream-dark bg-white overflow-x-auto no-scrollbar">
            {protocols.map(proto => (
              <button 
                key={proto.id}
                onClick={() => setActiveProtocolId(proto.id)}
                className={`px-4 py-3 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 whitespace-nowrap transition-colors border-b-2 ${activeProtocolId === proto.id ? 'text-terracotta border-terracotta bg-terracotta/5' : 'text-espresso-light/40 border-transparent hover:bg-cream/50'}`}
              >
                <SafeIcon name={proto.iconName} className="w-3.5 h-3.5" />
                {proto.title}
              </button>
            ))}
          </div>
          <div className="p-4 space-y-4">
            {activeProtocol?.tasks.map(item => (
              <ChecklistItem 
                key={item.id}
                item={item}
                progressData={product.checklist?.[item.id]}
                onToggle={() => {
                  const current = product.checklist?.[item.id];
                  const isDone = typeof current === 'object' ? current.completed : !!current;
                  const update = { ...current, completed: !isDone };
                  updateProduct(product.id, { checklist: { ...product.checklist, [item.id]: update } });
                }}
                onUpdateDetails={(details) => {
                  updateProduct(product.id, { checklist: { ...product.checklist, [item.id]: details } });
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCard;